import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { useCmsRoles } from "@/hooks/useCmsRoles";
import { api } from "@/lib/api";

const FIELDS = ["response_type", "client_id", "redirect_uri", "code_challenge", "code_challenge_method", "state", "scope", "resource"];

// OAuth consent for the ChatGPT publishing connector. Only a signed-in WUA admin can approve.
export default function OAuthAuthorize() {
  const { user, loading, login } = useAuth();
  const { isAdmin, loaded } = useCmsRoles();
  const params = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return Object.fromEntries(FIELDS.filter((k) => sp.get(k)).map((k) => [k, sp.get(k)]));
  }, []);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/oauth/authorize/info", params).then((r) => setInfo(r.data)).catch(() => setInfo({ ok: false, error: "This connection request is incomplete." }));
  }, [params]);
  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post("/oauth/authorize/approve", params);
      window.location.assign(r.data.redirect);
    } catch {
      setBusy(false);
      setInfo({ ok: false, error: "Approval failed. Sign in with an admin account and try again from ChatGPT." });
    }
  };
  const deny = () => {
    const u = new URL(params.redirect_uri);
    u.searchParams.set("error", "access_denied");
    if (params.state) u.searchParams.set("state", params.state);
    window.location.assign(u.toString());
  };
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-[#f7f4ee] px-4" data-testid="oauth-authorize">
      <Helmet><title>Connect ChatGPT | Which University Advisor</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <div className="w-full max-w-lg rounded-lg border border-slate-300 bg-white p-6">
        <h1 className="font-editorial text-2xl text-[#12312b]">Connect ChatGPT to subject-guide publishing</h1>
        {!info || loading || (user && !loaded) ? <p className="mt-4 text-slate-600">Checking the request…</p>
          : !info.ok ? <p className="mt-4 text-red-700" data-testid="oauth-error">{info.error}</p>
          : !user ? (
            <div className="mt-4"><p className="text-slate-700">Sign in with your WUA admin account to continue.</p>
              <button type="button" onClick={login} className="mt-4 rounded bg-[#175d51] px-4 py-2 text-white" data-testid="oauth-sign-in">Sign in</button></div>)
          : !isAdmin ? <p className="mt-4 text-red-700" data-testid="oauth-not-admin">Only WUA admins can connect a publishing tool. You are signed in as {user.email}.</p>
          : (
            <div className="mt-4 text-slate-700">
              <p><strong>{info.client_name}</strong> is asking to act as <strong>{user.email}</strong> to:</p>
              <ul className="mt-2 list-disc pl-5 text-sm">
                <li>read the subject-guide contract, roster and live guides</li>
                <li>check and publish subject guides that pass the quality rules</li>
              </ul>
              <p className="mt-3 text-sm text-slate-500">It cannot change other site data. Every upload is versioned and can be rolled back. You can revoke access on the Subjects page. Returns to {info.redirect_host}.</p>
              <div className="mt-5 flex gap-3">
                <button type="button" onClick={approve} disabled={busy} className="rounded bg-[#175d51] px-4 py-2 text-white" data-testid="oauth-approve">Allow</button>
                <button type="button" onClick={deny} className="rounded border px-4 py-2" data-testid="oauth-deny">Cancel</button>
              </div>
            </div>)}
      </div>
    </div>
  );
}
